import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { useLazyClubAdminGetBillingStatusQuery } from '../../features/SpeechAgendaApiSlice.ts'
import { showGlobalModal } from '../../features/GlobalNotificationModalSlice.ts'
import {
  clearPaywall,
  showPaywall,
} from '../../features/BillingPaywallSlice.ts'

const MAX_ATTEMPTS = 5
const RETRY_DELAY_MS = 1500

const GOOD_STATUSES = ['trialing', 'active']

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Handles the ?checkout=success|cancel return from Stripe Checkout on
// /club/:clubId. `isAdmin` must stay undefined until club details load so the
// param isn't consumed before we know whether the billing endpoint is callable.
export function useCheckoutReturn(
  clubId: string | undefined,
  isAdmin: boolean | undefined
) {
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [fetchBillingStatus] = useLazyClubAdminGetBillingStatusQuery()
  const handled = useRef(false)

  const checkout = searchParams.get('checkout')

  useEffect(() => {
    if (!clubId || !checkout || isAdmin === undefined || handled.current) {
      return
    }
    handled.current = true

    setSearchParams(
      (params) => {
        params.delete('checkout')
        return params
      },
      { replace: true }
    )

    if (!isAdmin) return

    // Canceled/abandoned checkout comes back as a fresh page load, so no 402
    // has set the paywall yet. Confirm the club actually needs a subscription
    // before surfacing the banner — a club in good standing (e.g. an active sub
    // whose owner backed out of the portal) shouldn't flash "read-only".
    if (checkout === 'cancel') {
      const surfacePaywallIfNeeded = async () => {
        try {
          const billing = await fetchBillingStatus(clubId).unwrap()
          if (
            billing.billingExempt ||
            GOOD_STATUSES.includes(billing.subscriptionStatus ?? '')
          ) {
            return
          }
        } catch {
          /* fall through — assume a subscription is needed */
        }
        dispatch(showPaywall(clubId))
      }
      void surfacePaywallIfNeeded()
      return
    }

    if (checkout !== 'success') return

    // The redirect races the checkout.session.completed webhook — the webhook
    // is the source of truth, so poll billing status briefly instead of
    // trusting the query param.
    const confirmSubscription = async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const billing = await fetchBillingStatus(clubId).unwrap()
          if (
            billing.billingExempt ||
            GOOD_STATUSES.includes(billing.subscriptionStatus ?? '')
          ) {
            dispatch(clearPaywall())
            dispatch(
              showGlobalModal({
                isOpen: true,
                title:
                  billing.subscriptionStatus === 'trialing'
                    ? 'Your free trial has started'
                    : 'Subscription active',
                description:
                  billing.subscriptionStatus === 'trialing'
                    ? 'Your club has full access for the next 90 days. Add a card before the trial ends to keep access - $19/month per club, cancel anytime from the billing page'
                    : 'Your club subscription is active. Manage it anytime from the billing page',
                buttonText: 'Got it',
                isSuccess: true,
              })
            )
            return
          }
        } catch {
          /* transient — keep retrying */
        }
        await sleep(RETRY_DELAY_MS)
      }
      dispatch(
        showGlobalModal({
          isOpen: true,
          title: 'Payment received',
          description:
            'Stripe confirmed your checkout, but it may take a minute for the subscription to activate. Check the billing page in Club Admin if it does not update shortly',
          buttonText: 'Got it',
          isSuccess: true,
        })
      )
    }

    void confirmSubscription()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, checkout, isAdmin])
}
